'use client';

import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { documentLabels, originInspectionDocumentTypes, isOriginInspectionDocument, isSettlementDocument, isTransportDocument, type DocumentType } from '../modules/document/document-types';
import styles from './workbench-document-menu.module.css';

const categories: readonly { label: string; documents: readonly { type: DocumentType; label: string }[] }[] = [
  { label: '交易成交类', documents: [
    { type: 'QUOTATION', label: 'Quotation' }, { type: 'PROFORMA_INVOICE', label: 'PI形式发票' },
    { type: 'PURCHASE_ORDER', label: 'PO采购订单' }, { type: 'SALES_CONTRACT', label: 'SC销售合同' },
    { type: 'SHIPPING_ORDER', label: 'SO订舱单' },
  ] },
  { label: '商业结汇类', documents: [
    { type: 'COMMERCIAL_INVOICE', label: 'CI商业发票' }, { type: 'PACKING_LIST', label: 'PL装箱单' },
    { type: 'CUSTOMS_INVOICE', label: '海关发票' }, { type: 'BENEFICIARY_CERTIFICATE', label: '受益人证明' },
  ] },
  { label: '运输类', documents: [
    { type: 'BILL_OF_LADING', label: 'BL提单' }, { type: 'AIR_WAYBILL', label: 'AWB空运单' },
    { type: 'MANIFEST', label: '舱单' }, { type: 'SHIPPING_ADVICE', label: '装运通知' }, { type: 'SHIPPING_INSTRUCTION', label: 'SI补料' },
  ] },
  { label: '保险类', documents: [{ type: 'INSURANCE_POLICY', label: '保险单' }] },
  { label: '原产地检验类', documents: originInspectionDocumentTypes.map(type => ({ type, label: documentLabels[type] })) },
];
const otherCategories = [ '金融结算类', '报关监管类'];

export function WorkbenchDocumentMenu({ documentType, locked, onDocumentTypeChange }: Readonly<{
  documentType: DocumentType; locked: boolean; onDocumentTypeChange(value: DocumentType): void;
}>) {
  const [openCategory, setOpenCategory] = useState<number | null>(null);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const menuId = useId();
  const navigationRef = useRef<HTMLElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const selectedCategory = isOriginInspectionDocument(documentType) ? 4 : documentType === 'INSURANCE_POLICY' ? 3 : isTransportDocument(documentType) ? 2 : isSettlementDocument(documentType) ? 1 : 0;

  useEffect(() => {
    if (openCategory === null) return;
    const updateDropdownPosition = () => {
      const trigger = triggerRef.current;
      const navigation = navigationRef.current;
      if (!trigger || !navigation) return;
      const triggerRect = trigger.getBoundingClientRect();
      const navigationRect = navigation.getBoundingClientRect();
      setDropdownLeft(triggerRect.left - navigationRect.left);
    };
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    const categories = categoriesRef.current;
    categories?.addEventListener('scroll', updateDropdownPosition);
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && !navigationRef.current?.contains(event.target)) setOpenCategory(null);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      categories?.removeEventListener('scroll', updateDropdownPosition);
      document.removeEventListener('pointerdown', dismiss);
    };
  }, [openCategory]);

  return <nav className={styles.navigation} aria-label="单据分类" ref={navigationRef}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpenCategory(null); }}
    onKeyDown={event => { if (openCategory !== null && event.key === 'Escape') { event.preventDefault(); setOpenCategory(null); triggerRef.current?.focus(); } }}>
    <div className={styles.categories} ref={categoriesRef}>
      {categories.map((category, index) => <button key={category.label}
        className={`${styles.category} ${selectedCategory === index ? styles.activeCategory : ''}`}
        type="button" id={`${menuId}-trigger-${index}`} aria-expanded={openCategory === index} aria-controls={`${menuId}-${index}`}
        onClick={event => {
          triggerRef.current = event.currentTarget;
          if (openCategory === index) {
            setOpenCategory(null);
            return;
          }
          const triggerRect = event.currentTarget.getBoundingClientRect();
          const navigationRect = navigationRef.current?.getBoundingClientRect();
          if (navigationRect) setDropdownLeft(triggerRect.left - navigationRect.left);
          setOpenCategory(index);
        }}>
        {category.label}<ChevronDown className={openCategory === index ? styles.expandedIcon : undefined} size={15} aria-hidden="true" />
      </button>)}
      {otherCategories.map(category => <button className={styles.category} type="button" key={category} disabled title="暂未开放">{category}</button>)}
    </div>
    {categories.map((category, index) => <div className={styles.dropdown} style={{ left: dropdownLeft }} id={`${menuId}-${index}`} key={category.label}
      role="group" aria-labelledby={`${menuId}-trigger-${index}`} hidden={openCategory !== index}>
      {category.documents.map(document => <button className={styles.document} type="button" key={document.type}
        aria-pressed={documentType === document.type} disabled={locked}
        onClick={() => { onDocumentTypeChange(document.type); setOpenCategory(null); triggerRef.current?.focus(); }}>
        <span>{document.label}</span>{documentType === document.type ? <Check size={16} aria-hidden="true" /> : null}
      </button>)}
      {locked ? <p className={styles.lockedHint}>当前交易的单据类型已锁定，请先开始新交易。</p> : null}
    </div>)}
  </nav>;
}
